import type { FixedProductInput } from '@vy/types';
import { extract, isNotEmptyArray, isNotNullOrUndefined, Optional } from '@vy/utils';
import { useState, type ComponentProps } from 'react';
import type { FieldPath } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import type { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { FixedProductNamePicker } from './FixedProductNamePicker';
import type { CatalogProducts } from '@vy/types';
import { creditTypeIdsFromSource } from '../../shared/credit-type-ids-from-source';
import { useCatalogProductsList } from '../../../../../../hooks/requests/use-catalog-products-list';
import { queryKeys } from '../../../../../../services/query-key/query-keys';

type FixedProductNameEditCellProps = {
  fixedProduct: FixedProductInput;
  field: FieldPath<FixedProductInput>;
  valueAsNumber?: boolean;
  productIndex: number;
} & Omit<ComponentProps<typeof VayuTextField>, 'name' | 'children' | 'onChange' | 'value'>;

export const FixedProductNameEditCell: React.FC<FixedProductNameEditCellProps> = ({
  fixedProduct,
  field,
  valueAsNumber = false,
  productIndex,
  ...rest
}) => {
  const queryClient = useQueryClient();
  const { data: catalogProductsData } = useCatalogProductsList();
  const catalogProducts = catalogProductsData?.catalogProducts ?? [];
  const catalogProductsNames = extract(catalogProducts, 'name');

  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { register, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const [serviceProductSelected, setServiceProductSelected] = useState<boolean>(false);

  const registerField = register(`fixedProducts.${productIndex}.${field}`, {
    valueAsNumber,
  });

  registerErrorField(registerField.name, CREATE_PLAN_TABS.serviceFees);

  const value = String(getValues(`fixedProducts.${productIndex}.${field}`) ?? '');

  const onNameChange = (newValue: Optional<string>) => {
    if (serviceProductSelected) {
      clearInnerFields();
    }

    const catalogProduct = catalogProducts.find((product) => product.name === newValue);

    if (isNotNullOrUndefined(catalogProduct)) {
      updateProductFields(catalogProduct);
      setServiceProductSelected(true);
    } else {
      setServiceProductSelected(false);
      clearInnerFields();
    }

    setValue(`fixedProducts.${productIndex}.${field}`, newValue ?? null);
  };

  const clearInnerFields = () => {
    setValue(`fixedProducts.${productIndex}.variants.0.description`, undefined);
    setValue(`fixedProducts.${productIndex}.erpMetadata`, undefined);
    setValue(`fixedProducts.${productIndex}.integrationEntities`, undefined);
    setValue(`fixedProducts.${productIndex}.externalIntegration`, undefined);
    setValue(`fixedProducts.${productIndex}.catalogProductId`, undefined);
    setValue(`fixedProducts.${productIndex}.creditTypeIds`, []);
  };

  const updateProductFields = (catalogProduct: CatalogProducts) => {
    setValue(`fixedProducts.${productIndex}.catalogProductId`, catalogProduct.id);
    setValue(`fixedProducts.${productIndex}.creditTypeIds`, creditTypeIdsFromSource(catalogProduct));

    if (catalogProduct.description) {
      setValue(`fixedProducts.${productIndex}.variants.0.description`, catalogProduct.description);
    }

    if (isNotEmptyArray(catalogProduct.integrationEntities)) {
      setValue(`fixedProducts.${productIndex}.integrationEntities`, catalogProduct.integrationEntities);
    }

    if (isNotEmptyArray(catalogProduct.integrationEntities)) {
      setValue(`fixedProducts.${productIndex}.erpMetadata`, fixedProduct.erpMetadata);
    }

    if (isNotNullOrUndefined(catalogProduct.externalIntegration)) {
      setValue(`fixedProducts.${productIndex}.externalIntegration`, {
        type: catalogProduct.externalIntegration.type,
        id: catalogProduct.externalIntegration.id,
      });
    }
  };

  const onProductCreated = async () => {
    await queryClient.invalidateQueries(queryKeys.catalogProducts());
  };

  return (
    <FixedProductNamePicker
      value={value}
      onChange={onNameChange}
      placeholder="Service name"
      catalogProductsNames={catalogProductsNames}
      onProductCreated={onProductCreated}
      {...rest}
    />
  );
};
